import type {
  KernelHandle,
  ProjectRuntimeState,
} from '#types/types';

type RuntimePointBeingDragged = ProjectRuntimeState['pointBeingDragged'];
export type RuntimeUpdater<T> = T | ((previous: T) => T);

const DEFAULT_PAN_OFFSET: { x: number; y: number } = { x: 0, y: 0 };
const DEFAULT_ZOOM_LEVEL = 1;

const POINT_NAMES = new Set([
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'top',
  'bottom',
  'left',
  'right',
] as const);

const normalizeProjectId = (projectId: string): string => projectId.trim();

const normalizeLayerIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

const normalizeLayerId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizePointBeingDragged = (
  value: unknown
): RuntimePointBeingDragged => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as { layerId?: unknown; pointName?: unknown };
  const layerId = normalizeLayerId(candidate.layerId);
  if (!layerId) return null;
  if (typeof candidate.pointName !== 'string' || !POINT_NAMES.has(candidate.pointName as never)) {
    return null;
  }
  return {
    layerId,
    pointName: candidate.pointName as NonNullable<RuntimePointBeingDragged>['pointName'],
  };
};

const normalizePanOffset = (value: unknown): { x: number; y: number } => {
  if (
    !value ||
    typeof value !== 'object' ||
    !Number.isFinite((value as { x?: unknown }).x) ||
    !Number.isFinite((value as { y?: unknown }).y)
  ) {
    return DEFAULT_PAN_OFFSET;
  }
  return {
    x: Number((value as { x: number }).x),
    y: Number((value as { y: number }).y),
  };
};

const normalizeZoomLevel = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_ZOOM_LEVEL;
  return value;
};

const normalizeBoolean = (value: unknown): boolean => value === true;

const normalizeRightSidebarTab = (
  value: unknown
): NonNullable<ProjectRuntimeState['rightSidebarTab']> => {
  if (value === 'settings') return 'settings';
  return value === 'models' ? 'models' : 'layers';
};

export const readProjectRuntimeField = <
  K extends keyof ProjectRuntimeState,
  T = NonNullable<ProjectRuntimeState[K]>
>(
  kernel: KernelHandle,
  projectId: string,
  key: K,
  fallback: T,
  normalize: (value: unknown) => T
): T => {
  const normalizedProjectId = normalizeProjectId(projectId);
  if (!normalizedProjectId) return fallback;
  return normalize(kernel.readProjectRuntimeState(normalizedProjectId)[key]);
};

export const patchProjectRuntimeField = <
  K extends keyof ProjectRuntimeState,
  T = NonNullable<ProjectRuntimeState[K]>
>(
  kernel: KernelHandle,
  projectId: string,
  key: K,
  updater: RuntimeUpdater<T>,
  fallback: T,
  normalize: (value: unknown) => T
): void => {
  const normalizedProjectId = normalizeProjectId(projectId);
  if (!normalizedProjectId) return;
  const previous = readProjectRuntimeField(kernel, normalizedProjectId, key, fallback, normalize);
  const nextRaw = typeof updater === 'function'
    ? (updater as (previous: T) => T)(previous)
    : updater;
  const nextValue = normalize(nextRaw);
  kernel.patchProjectRuntimeState(normalizedProjectId, {
    [key]: nextValue,
  } as Partial<ProjectRuntimeState>);
};

export const getProjectSelectedLayersId = (
  kernel: KernelHandle,
  projectId: string
): string[] =>
  readProjectRuntimeField(kernel, projectId, 'selectedLayersId', [], normalizeLayerIds);

export const setProjectSelectedLayersId = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<string[]>
): void => {
  patchProjectRuntimeField(kernel, projectId, 'selectedLayersId', updater, [], normalizeLayerIds);
};

export const getProjectDraggedLayerId = (
  kernel: KernelHandle,
  projectId: string
): string | null =>
  readProjectRuntimeField<'draggedLayerId', string | null>(
    kernel,
    projectId,
    'draggedLayerId',
    null,
    normalizeLayerId
  );

export const setProjectDraggedLayerId = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<string | null>
): void => {
  patchProjectRuntimeField<'draggedLayerId', string | null>(
    kernel,
    projectId,
    'draggedLayerId',
    updater,
    null,
    normalizeLayerId
  );
};

export const getProjectIsResizingLayer = (
  kernel: KernelHandle,
  projectId: string
): boolean =>
  readProjectRuntimeField(kernel, projectId, 'isResizingLayer', false, normalizeBoolean);

export const setProjectIsResizingLayer = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<boolean>
): void => {
  patchProjectRuntimeField(kernel, projectId, 'isResizingLayer', updater, false, normalizeBoolean);
};

export const getProjectPointBeingDragged = (
  kernel: KernelHandle,
  projectId: string
): RuntimePointBeingDragged =>
  readProjectRuntimeField<'pointBeingDragged', RuntimePointBeingDragged>(
    kernel,
    projectId,
    'pointBeingDragged',
    null,
    normalizePointBeingDragged
  );

export const setProjectPointBeingDragged = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<RuntimePointBeingDragged>
): void => {
  patchProjectRuntimeField<'pointBeingDragged', RuntimePointBeingDragged>(
    kernel,
    projectId,
    'pointBeingDragged',
    updater,
    null,
    normalizePointBeingDragged
  );
};

export const getProjectIsPanning = (
  kernel: KernelHandle,
  projectId: string
): boolean =>
  readProjectRuntimeField(kernel, projectId, 'isPanning', false, normalizeBoolean);

export const setProjectIsPanning = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<boolean>
): void => {
  patchProjectRuntimeField(kernel, projectId, 'isPanning', updater, false, normalizeBoolean);
};

export const getProjectPanOffset = (
  kernel: KernelHandle,
  projectId: string
): { x: number; y: number } =>
  readProjectRuntimeField(kernel, projectId, 'panOffset', DEFAULT_PAN_OFFSET, normalizePanOffset);

export const setProjectPanOffset = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<{ x: number; y: number }>
): void => {
  patchProjectRuntimeField(
    kernel,
    projectId,
    'panOffset',
    updater,
    DEFAULT_PAN_OFFSET,
    normalizePanOffset
  );
};

export const getProjectZoomLevel = (
  kernel: KernelHandle,
  projectId: string
): number =>
  readProjectRuntimeField(kernel, projectId, 'zoomLevel', DEFAULT_ZOOM_LEVEL, normalizeZoomLevel);

export const setProjectZoomLevel = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<number>
): void => {
  patchProjectRuntimeField(
    kernel,
    projectId,
    'zoomLevel',
    updater,
    DEFAULT_ZOOM_LEVEL,
    normalizeZoomLevel
  );
};

export const setProjectRightSidebarTab = (
  kernel: KernelHandle,
  projectId: string,
  updater: RuntimeUpdater<NonNullable<ProjectRuntimeState['rightSidebarTab']>>
): void => {
  patchProjectRuntimeField(
    kernel,
    projectId,
    'rightSidebarTab',
    updater,
    'layers',
    normalizeRightSidebarTab
  );
};

